import { InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack } from '@mui/material';
import { FC, KeyboardEvent, MouseEvent, useMemo } from 'react';
import { quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsNotAllocReason } from 'src/entities/PlanningsNotAllocReasons.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { NotAllocReasonPresenter } from 'src/pages/V3Plans/presenters/NotAllocReasonPresenter';
import { getInfoMetricValue } from 'src/utils/metricHelpers';

import { activeInfoMetrics, DisplayMetric, infoMetricLabel } from './displayMetricKeys';
import { EMPTY_INFO_VALUE, InfoMetricList } from './InfoMetricList';
import { LaneGroup } from './laneGroups';
import { buildLaneLabel } from './laneLabel';
import { SameLocationTag } from './SameLocationTag';
import { SameLocationInfo } from './sameLocationVM';
import { SUB_TEXT_COLOR, SUB_TEXT_WEIGHT } from './unassignedTextColors';


export type LaneCardProps = {
  group: LaneGroup;
  displayMetrics: Set<DisplayMetric>;
  customInputFieldNames: string[];
  /** 配下の伝票 ID のうち選択済みの件数。一部選択を数字で見せるため実数で受ける */
  selectedCount: number;
  onToggleSelection: () => void;
  /** mergeLaneNotAllocReasons の結果 */
  notAllocReasons?: PlanningsNotAllocReason[];
  notAllocDeliveries?: PlanningsDeliveryEntity[];
  notAllocTrucks?: PlanningsTruckEntity[];
  notAllocDrivers?: PlanningsDriverEntity[];
  /**
   * このレーンの詳細を開く。未指定なら info アイコンを出さない
   * （開く先が無い画面でボタンだけ置かない）。
   */
  onShowDetail?: () => void;
  /**
   * 配下の伝票がまとめて同一地点なので、レーンでも同じタグを出す
   * (log_web_issues#318)。配下の重複排除は mergeSameLocationInfos が済ませて渡す。
   */
  sameLocation?: SameLocationInfo;
  /**
   * タグから回転行を選択する。行を押したときは 1 件、バッジを押したときは全件
   * (log_web_issues#323)。未指定なら行を押せなくする。
   */
  onSelectVehicleRows?: (rowIds: readonly string[]) => void;
};

export const LaneCard: FC<LaneCardProps> = ({
  group, displayMetrics, customInputFieldNames, selectedCount, onToggleSelection,
  notAllocReasons, notAllocDeliveries, notAllocTrucks, notAllocDrivers, onShowDetail,
  sameLocation, onSelectVehicleRows,
}) => {
  const total = group.items.length;
  const allSelected = total > 0 && selectedCount === total;
  const someSelected = selectedCount > 0 && selectedCount < total;
  const hasReasons = (notAllocReasons?.length ?? 0) > 0;

  // 降地の名称だけ。積地を入れない理由は buildLaneLabel の Why not を参照
  const laneLabel = buildLaneLabel(group);

  const metrics: string[] = [];
  if (displayMetrics.has('quantity')) metrics.push(`${quantityValueText(group.totalCases)} cs`);
  if (displayMetrics.has('weight')) metrics.push(`${weightValueText(group.totalWeight)}kg`);
  if (displayMetrics.has('volume')) metrics.push(`${volumeValueText(group.totalVolume)}m³`);

  // 伝票No / 積地 / 降地はカード上部で別に描くのでここから除く（要件どおり）。
  const laneInfoMetrics = useMemo(
    () => activeInfoMetrics([...displayMetrics], customInputFieldNames)
      .filter((m) => m !== 'invoice' && m !== 'origin' && m !== 'destination'),
    [displayMetrics, customInputFieldNames],
  );

  const infoEntries = useMemo(
    () => laneInfoMetrics.map((metric) => {
      const values = [...new Set(group.items.map((item) => getInfoMetricValue(metric, item)).filter(Boolean))];
      return { key: metric, label: infoMetricLabel(metric), value: values.join(' / ') || EMPTY_INFO_VALUE };
    }),
    [group.items, laneInfoMetrics],
  );

  // role="button" の div は Space でページがスクロールするので preventDefault が必要。
  const handleSelectionKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onToggleSelection();
  };

  return (
    <Box
      sx={{
        borderRadius: 1,
        bgcolor: 'hsl(var(--card))',
        p: 1.25,
        boxShadow: '0 0 0 1px hsl(var(--border)/0.7), 0 2px 4px hsl(var(--foreground)/0.04)',
        userSelect: 'none',
        ...(allSelected && { outline: '2px solid hsl(var(--primary))', outlineOffset: '1px' }),
      }}
    >
      {/* 並びは伝票カード (UnassignedCardContent) と揃える。表示単位を切り替えたときに
          同じタグが上下に飛ぶと、別物が出たように見える */}
      {sameLocation && (
        <SameLocationTag info={sameLocation} onSelectVehicles={onSelectVehicleRows} />
      )}
      {/* 未割当理由はカードの一番上に独立した行で出す。ヘッダ行に混ぜると
          降地名称と幅を奪い合い、どちらも省略記号に埋もれる */}
      {hasReasons && (
        <Box sx={{ mb: 0.5 }}>
          <NotAllocReasonPresenter
            variant="badge"
            filteredNotAllocReasons={notAllocReasons ?? []}
            notAllocDeliveries={notAllocDeliveries ?? []}
            notAllocTrucks={notAllocTrucks ?? []}
            notAllocDrivers={notAllocDrivers ?? []}
          />
        </Box>
      )}
      {/*
        ヘッダ行は非操作の flex コンテナ。ハンドラを持つ「選択面」「シェブロン」を
        兄弟にすることで、どの操作も他へ伝播しない。stopPropagation はクリック経路しか
        守れないので、構造で解決する。
      */}
      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
        <Box
          role="button"
          tabIndex={0}
          aria-pressed={allSelected ? true : (someSelected ? 'mixed' : false)}
          onClick={onToggleSelection}
          onKeyDown={handleSelectionKeyDown}
          sx={{ minWidth: 0, flex: 1, cursor: 'pointer' }}
        >
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, color: 'hsl(var(--card-foreground))' }}>
            {laneLabel}
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5, fontSize: '12px', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', color: SUB_TEXT_COLOR, fontWeight: SUB_TEXT_WEIGHT }}>
            <Box component="span">{someSelected ? `${selectedCount} / ${total} 件` : `${total} 件`}</Box>
            {metrics.map((m) => <Box component="span" key={m}>{m}</Box>)}
          </Stack>
          <InfoMetricList entries={infoEntries} />
        </Box>
        {/* 伝票の展開はやめて詳細を開く口にした。伝票ごとに触りたいときは
            「伝票単位」タブへ切り替える（地点単位は地点ごとにまとめて扱う表示） */}
        {onShowDetail && (
          <IconButton
            size="small"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              // 選択面と兄弟だが、詳細を開くだけで選択は変えない
              e.stopPropagation();
              onShowDetail();
            }}
            aria-label="レーンの詳細を表示"
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <InfoOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
};

export default LaneCard;
